import * as React from 'react';
import { Button, Form, ProgressBar, Table } from 'react-bootstrap';
import { getLocalization } from 'global/global';
import { useAppSelector } from 'index';
import { ActivityItem, ActivityPeriod } from 'Interfaces/Admin';
import { fetchActivity } from 'api/adminApi';
import { adminUsersSelector } from 'reducers/admin/selectors';
import { buildActivityTable, columnTotals } from './activityTable';

/** Replaces ActivityView.js. */
export default function Activity() {
  const users = useAppSelector(adminUsersSelector);
  const account = useAppSelector((state) => state.admin.account);
  const ui = useAppSelector((state) => state.admin.ui);

  const [period, setPeriod] = React.useState<ActivityPeriod>('week');
  const [subform, setSubform] = React.useState(false);
  const [countUnique, setCountUnique] = React.useState(false);
  const [items, setItems] = React.useState<ActivityItem[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  /** The grid selection scopes the report, as it did through detailsView. */
  const scope = React.useMemo(() => {
    if (ui.gridSelection.length === 0) {
      return {};
    }
    return ui.gridMode === 'groups'
      ? { groups: ui.gridSelection }
      : { users: ui.gridSelection };
  }, [ui.gridMode, ui.gridSelection]);

  const load = React.useCallback(() => {
    setLoading(true);
    fetchActivity({
      ...scope,
      period,
      subform: subform ? 1 : 0,
      countUnique: countUnique ? 1 : 0,
    })
      .then((result) => setItems(Array.isArray(result) ? result : []))
      .catch((e) => {
        console.log(e);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [scope, period, subform, countUnique]);

  React.useEffect(() => {
    load();
  }, []);

  /** Only the selected users are reported on, when a selection exists. */
  const reportedUsers = React.useMemo(() => {
    if (ui.gridSelection.length === 0) {
      return users;
    }
    return ui.gridMode === 'groups'
      ? users.filter((u) => ui.gridSelection.includes(u.groupId))
      : users.filter((u) => ui.gridSelection.includes(u.id));
  }, [users, ui.gridMode, ui.gridSelection]);

  const table = React.useMemo(
    () => buildActivityTable(items || [], reportedUsers, period, account.logDate),
    [items, reportedUsers, period, account.logDate],
  );
  const totals = React.useMemo(() => columnTotals(table), [table]);

  return (
    <div className="admin-activity">
      <Form className="form-inline" onSubmit={(e) => e.preventDefault()}>
        <Form.Group>
          <Form.Label>{getLocalization('period')}</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            id="activity-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as ActivityPeriod)}
          >
            <option value="week">{getLocalization('periodWeek')}</option>
            <option value="month">{getLocalization('periodMonth')}</option>
            <option value="year">{getLocalization('periodYear')}</option>
          </Form.Control>
        </Form.Group>
        <Form.Check
          id="activity-subform"
          label={getLocalization('includeSubformCount')}
          checked={subform}
          onChange={(e) => setSubform(e.target.checked)}
        />
        <Form.Check
          id="activity-unique"
          label={getLocalization('countUniqueDataPoint')}
          checked={countUnique}
          onChange={(e) => setCountUnique(e.target.checked)}
        />
        <Button size="sm" onClick={load}>{getLocalization('submit')}</Button>
      </Form>

      {loading && <ProgressBar striped now={100} animated />}

      {!loading && items !== null && table.rows.length === 0 && (
        <p>{getLocalization('noActivity')}</p>
      )}

      {!loading && table.rows.length > 0 && (
        <Table striped bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th>{getLocalization('userName')}</th>
              {table.columns.map((key) => <th key={key}>{key}</th>)}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.username}>
                <td>{row.username}</td>
                {table.columns.map((key) => <td key={key}>{row[key]}</td>)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>{getLocalization('total')}</th>
              {table.columns.map((key) => <th key={key}>{totals[key]}</th>)}
            </tr>
          </tfoot>
        </Table>
      )}
    </div>
  );
}
